import { baseDroneApi } from '@src/core/features';
import { baseProfileApi } from '@src/core/features/profile/redux/api';
import type {
  IAdjustDroneMaintenanceRequest,
  IGetDroneFlightDispatcherResponse,
  IGetDroneHistoryResponse,
  IGetDroneMaintenanceResponse,
  IGetDroneOrderHistoryRequest,
  IGetDroneOrderHistoryResponse,
  IRegisterDroneRequest,
  IRegisterDroneResponse,
  IResetPartDroneRequest,
  IUpdateDroneFirmwareRequest,
} from './types';

export const droneFeatureApi = baseDroneApi.injectEndpoints({
  endpoints: (builder) => ({
    getDroneHistory: builder.query<IGetDroneHistoryResponse, number>({
      query: (droneId) => ({
        url: `/api/v1/drone/${droneId}/orders`,
        method: 'GET',
      }),
      providesTags: ['GetDroneHistory'],
    }),
    getDroneOrderHistory: builder.query<IGetDroneOrderHistoryResponse, IGetDroneOrderHistoryRequest>({
      query: ({ id, ...params }) => ({
        url: `/api/v1/drone/${id}/orders/paged`,
        method: 'GET',
        params,
      }),
      keepUnusedDataFor: 0,
    }),
    deleteDrone: builder.mutation<void, number>({
      query: (droneId) => ({
        url: `/api/v1/drone/archive/${droneId}`,
        method: 'PUT',
      }),
      invalidatesTags: ['GetDrones'],
    }),
    registerDrone: builder.mutation<IRegisterDroneResponse, IRegisterDroneRequest>({
      query: (data) => ({
        url: `/api/v1/drone/activate`,
        method: 'PUT',
        data
      }),
      invalidatesTags: ['GetDrones'],
      async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          dispatch(baseProfileApi.util.invalidateTags(['Hubs']));
        } catch {
        }
      },
    }),
    resetPartDrone: builder.mutation<void, IResetPartDroneRequest>({
      query: ({ id, part }) => ({
        url: `/api/v1/drone/${id}/${part}/reset`,
        method: 'PUT',
      }),
      invalidatesTags: (_, __, { id }) => ['GetDroneHistory', { type: 'GetDroneMaintenance', id }],
    }),
    flightSummaryById: builder.mutation<string, number>({
      query: (id) => ({
        url: `/api/v1/flight-summaries/${id}/download`,
        method: 'GET',
        responseType: 'blob',
      }),
      transformResponse: (blob: Blob) => {
        const fileBlob = new Blob([blob], { type: blob.type });
        return URL.createObjectURL(fileBlob);
      },
    }),
    flightSummaryAll: builder.mutation<string, void>({
      query: () => ({
        url: `/api/v1/flight-summaries/download`,
        method: 'GET',
        responseType: 'blob',
      }),
      transformResponse: (blob: Blob) => {
        const fileBlob = new Blob([blob], { type: blob.type });
        return URL.createObjectURL(fileBlob);
      },
    }),
    getDroneMaintenance: builder.query<IGetDroneMaintenanceResponse, number>({
      query: (droneId) => ({
        url: `/api/v1/drone/${droneId}/maintenance`,
        method: 'GET',
      }),
      providesTags: (_result, _error, droneId) => [{ type: 'GetDroneMaintenance', id: droneId }],
    }),
    adjustDroneMaintenance: builder.mutation<void, IAdjustDroneMaintenanceRequest>({
      query: ({ id, part, type, value }) => ({
        url: `/api/v1/drone/${id}/parts/adjust`,
        method: 'POST',
        data: { part, type, value },
      }),
      invalidatesTags: (_result, _error, { id }) => [{ type: 'GetDroneMaintenance', id }],
    }),

    getDroneFlightDispatcher: builder.query<IGetDroneFlightDispatcherResponse, number>({
      query: (droneId) => ({
        url: `/api/v1/drone/${droneId}/flightdispatcher`,
        method: 'GET',
      }),
      providesTags: (_r, _e, id) => [{ type: 'DroneFirmware', id }],
    }),

    updateDroneFirmware: builder.mutation<void, IUpdateDroneFirmwareRequest>({
      query: ({ droneId }) => ({
        url: `/api/v1/drone/${droneId}/flightdispatcher/update`,
        method: 'POST',
        data: {},
      }),
      invalidatesTags: (_r, _e, { droneId }) => [
        { type: 'DroneFirmware', id: droneId },
        'GetDrones',
      ],
    }),
  }),
});

export const { 
  useGetDroneHistoryQuery, 
  useGetDroneMaintenanceQuery,
  useAdjustDroneMaintenanceMutation,
  useFlightSummaryAllMutation,
  useFlightSummaryByIdMutation,
  useGetDroneOrderHistoryQuery,

  useDeleteDroneMutation,
  useRegisterDroneMutation,
  useResetPartDroneMutation,

  useGetDroneFlightDispatcherQuery,
  useUpdateDroneFirmwareMutation,
} = droneFeatureApi;